import React from 'react'
import PropTypes from 'prop-types'
import {
  Card,
  Typography,
  Space,
  Divider,
  Table,
  Tag,
  Collapse,
  Button,
  message
} from 'antd'
import { CopyOutlined } from '@ant-design/icons'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism'

const { Title, Text, Paragraph } = Typography
const { Panel } = Collapse

export const ApiDocumentation = ({ token }) => {
  const handleCopyRequest = (request) => {
    navigator.clipboard.writeText(request)
    message.success('Request berhasil disalin')
  }

  const apiEndpoints = [
    {
      id: 'coupon-check',
      method: 'GET',
      endpoint: '/coupons/check/{code}',
      title: 'Check Coupon Validity',
      description: 'Mengecek apakah coupon masih valid atau sudah expired',
      auth: 'Required',
      parameters: [
        {
          name: 'code',
          type: 'string',
          location: 'path',
          required: true,
          description: 'Kode coupon yang akan dicek'
        },
        {
          name: 'phone',
          type: 'string',
          location: 'query',
          required: false,
          description: 'Nomor telepon untuk tracking claims per phone'
        }
      ],
      exampleRequest: `curl -X GET "https://api-legalitas.opim.co.id/api/v1/public/coupons/check/{code}?phone=08123456789" \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
      exampleResponses: [
        {
          title: 'Valid Coupon (Not Expired)',
          code: `{
  "statusCode": 200,
  "path": "/api/v1/public/coupons/check/{code}",
  "duration": "3ms",
  "method": "GET",
  "data": {
    "code": "{code}",
    "is_valid": true,
    "is_expired": false,
    "expires_at": "2025-09-28T09:10:58.824Z",
    "description": "Diskon 1000 IDR",
    "discount_value": 1000,
    "discount_type": "price",
    "phone_claim_count": 2,
    "total_claim_count": 150,
    "limit_claim": 1000,
    "is_limit_reached": false,
    "limit_claim_per_qr": 500,
    "is_phone_limit_reached": false,
    "is_qr_limit_reached": false
  }
}`
        },
        {
          title: 'Valid Coupon (Expired)',
          code: `{
  "statusCode": 200,
  "path": "/api/v1/public/coupons/check/{code}",
  "duration": "3ms",
  "method": "GET",
  "data": {
    "code": "{code}",
    "is_valid": false,
    "is_expired": true,
    "expires_at": "2025-03-27T04:14:27.798Z",
    "description": "Diskon 1%",
    "discount_value": 1,
    "discount_type": "percent",
    "phone_claim_count": null,
    "total_claim_count": 50,
    "limit_claim": null,
    "is_limit_reached": false,
    "limit_claim_per_qr": null,
    "is_phone_limit_reached": false,
    "is_qr_limit_reached": false
  }
}`
        },
        {
          title: 'Valid Coupon (Limit Reached)',
          code: `{
  "statusCode": 200,
  "path": "/api/v1/public/coupons/check/{code}",
  "duration": "3ms",
  "method": "GET",
  "data": {
    "code": "{code}",
    "is_valid": false,
    "is_expired": false,
    "expires_at": "2025-12-31T23:59:59.000Z",
    "description": "Diskon 50%",
    "discount_value": 50,
    "discount_type": "percent",
    "phone_claim_count": 1,
    "total_claim_count": 1000,
    "limit_claim": 1000,
    "is_limit_reached": true,
    "limit_claim_per_qr": 300,
    "is_phone_limit_reached": false,
    "is_qr_limit_reached": true
  }
}`
        },
        {
          title: 'Valid Coupon (Phone Limit Reached)',
          code: `{
  "statusCode": 200,
  "path": "/api/v1/public/coupons/check/{code}",
  "duration": "3ms",
  "method": "GET",
  "data": {
    "code": "{code}",
    "is_valid": false,
    "is_expired": false,
    "expires_at": "2025-12-31T23:59:59.000Z",
    "description": "Diskon 25%",
    "discount_value": 25,
    "discount_type": "percent",
    "phone_claim_count": 3,
    "total_claim_count": 200,
    "limit_claim": 1000,
    "is_limit_reached": false,
    "limit_claim_per_qr": 3,
    "is_phone_limit_reached": true,
    "is_qr_limit_reached": false
  }
}`
        },
        {
          title: 'Invalid Coupon (Not Found)',
          code: `{
  "message": "Coupon not found",
  "error": "Not Found",
  "statusCode": 404
}`
        }
      ]
    }
  ]

  return (
    <Card>
      <Space direction='vertical' style={{ width: '100%' }}>
        <Title level={4}>API Documentation</Title>
        <Paragraph>
          Base URL: <Text code>https://api-legalitas.opim.co.id/api/v1/public</Text>
        </Paragraph>
        <Divider orientation='left'>Authentication</Divider>
        <Paragraph>
          Semua permintaan API memerlukan API key yang valid. Sertakan API key
          di header:
        </Paragraph>
        <Text code>Authorization: Bearer YOUR_API_KEY</Text>
        <Divider orientation='left'>Available Endpoints</Divider>
        <Collapse style={{ marginTop: '16px' }}>
          {apiEndpoints.map((endpoint) => (
            <Panel
              header={
                <Space>
                  <Tag
                    color={
                      endpoint.method === 'GET'
                        ? 'blue'
                        : endpoint.method === 'POST'
                          ? 'green'
                          : 'orange'
                    }
                  >
                    {endpoint.method}
                  </Tag>
                  <Text code>{endpoint.endpoint}</Text>
                  <Text>{endpoint.title}</Text>
                </Space>
              }
              key={endpoint.id}
            >
              <Space direction='vertical' style={{ width: '100%' }}>
                <div>
                  <Title level={5}>Description</Title>
                  <Paragraph>{endpoint.description}</Paragraph>
                </div>

                <div>
                  <Title level={5}>Parameters</Title>
                  <Table
                    columns={[
                      {
                        title: 'Name',
                        dataIndex: 'name',
                        key: 'name',
                        render: (name) => <Text code>{name}</Text>
                      },
                      {
                        title: 'Type',
                        dataIndex: 'type',
                        key: 'type'
                      },
                      {
                        title: 'Location',
                        dataIndex: 'location',
                        key: 'location'
                      },
                      {
                        title: 'Required',
                        dataIndex: 'required',
                        key: 'required',
                        render: (required) => (
                          <Tag color={required ? 'red' : 'default'}>
                            {required ? 'Required' : 'Optional'}
                          </Tag>
                        )
                      },
                      {
                        title: 'Description',
                        dataIndex: 'description',
                        key: 'description'
                      }
                    ]}
                    dataSource={endpoint.parameters}
                    pagination={false}
                    size='small'
                    rowKey='name'
                  />
                </div>

                <div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}
                  >
                    <Title level={5} style={{ margin: 0 }}>
                      Example Request
                    </Title>
                    <Button
                      type='primary'
                      size='small'
                      onClick={() => handleCopyRequest(endpoint.exampleRequest)}
                      title='Copy request'
                      style={{
                        backgroundColor: '#21BADF',
                        borderColor: '#21BADF'
                      }}
                    >
                      <CopyOutlined /> Copy
                    </Button>
                  </div>
                  <SyntaxHighlighter
                    language='bash'
                    style={tomorrow}
                    customStyle={{
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  >
                    {endpoint.exampleRequest}
                  </SyntaxHighlighter>
                </div>

                <div>
                  <Title level={5}>Example Responses</Title>
                  {endpoint.exampleResponses.map((response, index) => (
                    <div key={index} style={{ marginBottom: '16px' }}>
                      <Text
                        strong
                        style={{
                          fontSize: '12px',
                          display: 'block',
                          marginBottom: '8px'
                        }}
                      >
                        {response.title}
                      </Text>
                      <SyntaxHighlighter
                        language='json'
                        style={tomorrow}
                        customStyle={{
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      >
                        {response.code}
                      </SyntaxHighlighter>
                    </div>
                  ))}
                </div>
              </Space>
            </Panel>
          ))}
        </Collapse>
      </Space>
    </Card>
  )
}

ApiDocumentation.propTypes = {
  token: PropTypes.string
}
